import { useMemo, FC } from 'react';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import { useQueryClient, useMutation } from 'react-query';
import { IconButton, Menu, MenuItem } from '@material-ui/core';
import { usePopover } from '../../../../hooks/use-popover';
import { DotsVertical as DotsVerticalIcon } from '../../../../icons/dots-vertical';
import { Flag as FlagIcon } from '../../../../icons/flag';
import { Ban as DeactivateItemIcon } from '../../../../icons/ban';
import { Trash as TrashIcon } from '../../../../icons/trash';
import { useDialog } from '../../../../hooks/use-dialog';
import { JobFlagDialog } from '../../../../components/job/job-flag-dialog';
import { FlagType, Job } from '../../../../types/job';
import { JobRevertFlagDialog } from '../../../../components/job/job-revert-flag-dialog';
import { ConfirmationDialog } from '../../../../components/confirmation-dialog';
import { useAuth } from '../../../../hooks/use-auth';
import { useAxios } from '../../../../hooks/use-axios';
import { ResponseData } from '../../../../types/axios';
import { Item } from '../../../../types/item';
import logger from '../../../../utils/logger';

interface JobsMenuProps {
  job: Job;
}

export const JobsMenu: FC<JobsMenuProps> = ({ job }) => {
  const [anchorRef, open, handleOpen, handleClose] = usePopover();
  const [openFlag, handleOpenFlag, handleCloseFlag] = useDialog();
  const [openRevertFlag, handleOpenRevertFlag, handleCloseRevertFlag] = useDialog();
  const [deactivateOpen, handleDeactivateOpen, handleDeactivateClose] = useDialog();
  const [deleteOpen, handleDeleteOpen, handleDeleteClose] = useDialog();

  const queryClient = useQueryClient();
  const { axios } = useAxios();
  const { tenant } = useAuth();

  const handleFlag = (): void => {
    handleClose();
    handleOpenFlag();
  };

  const handleRevertFlag = (): void => {
    handleClose();
    handleOpenRevertFlag();
  };

  const deactivateMutation = useMutation(
    async () => {
      if (!tenant) {
        throw new Error('Tenant is missing');
      }
      const url = `/items/${job.item?.id}`;

      const data = {
        publicMetadata: {
          deactivated: true,
        },
      };

      return axios.put<ResponseData<Item>>(url, data);
    },
    {
      onSuccess: () => {
        toast.success('Item was deactivated');
        queryClient.invalidateQueries('jobs');
      },
      onError: (err: any) => {
        logger(err);
        toast.error(err.message || 'Deactivating item failed');
      },
      onSettled: () => {
        handleDeactivateClose();
      },
    },
  );

  const handleDeactivate = () => {
    deactivateMutation.mutate();
  };

  const deleteMutation = useMutation(
    async () => axios.post(`/jobs/${job.id}/delete`),
    {
      onSuccess: () => {
        toast.success('Event deleted');
        queryClient.invalidateQueries('jobs');
        queryClient.invalidateQueries(['job', job.id]);
        queryClient.invalidateQueries('item-events');
      },
      onError: (err: any) => {
        const msg = err?.response?.data?.error?.message || 'Deleting event failed';
        logger(msg);
        toast.error(msg);
      },
      onSettled: () => {
        handleDeleteClose();
      },
    },
  );

  const handleDelete = () => {
    deleteMutation.mutate();
  };

  const handleDeleteClick = () => {
    handleClose();
    handleDeleteOpen();
  };

  const flagJobAction = useMemo(
    () =>
      job.flag === FlagType.Incorrect ? (
        <MenuItem onClick={handleRevertFlag}>
          <FlagIcon color="action" sx={{ mr: 1 }} />
          Revert Report
        </MenuItem>
      ) : (
        <MenuItem onClick={handleFlag}>
          <FlagIcon color="error" sx={{ mr: 1 }} />
          Report Event
        </MenuItem>
      ),

    [job],
  );

  const deactivateItemAction = useMemo(
    () =>
      tenant.isActive &&
      job.item &&
      !job.item.isRegistered &&
      !job.item?.publicMetadata?.deactivated && (
        <MenuItem onClick={handleDeactivateOpen}>
          <DeactivateItemIcon color="action" sx={{ mr: 1 }} />
          Deactivate Item
        </MenuItem>
      ),

    [job],
  );

  return (
    <>
      <IconButton onClick={handleOpen} ref={anchorRef}>
        <DotsVerticalIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        open={open}
        onClose={handleClose}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {flagJobAction}
        {deactivateItemAction}
        <MenuItem onClick={handleDeleteClick}>
          <TrashIcon color="action" sx={{ mr: 1 }} />
          Delete Event
        </MenuItem>
      </Menu>
      <JobFlagDialog job={job} open={openFlag} onClose={handleCloseFlag} />
      <JobRevertFlagDialog job={job} open={openRevertFlag} onClose={handleCloseRevertFlag} />
      <ConfirmationDialog
        message={`Are you sure you want to deactivate this item: „${job.item?.uniqueId}“?`}
        onCancel={handleDeactivateClose}
        onConfirm={handleDeactivate}
        confirmationInProgress={deactivateMutation.isLoading}
        open={deactivateOpen}
        title="Deactivate Item"
        variant="warning"
      />
      <ConfirmationDialog
        message="Are you sure you want to delete this event and its tasks? This hides them from listings — assets and ML data are not removed."
        onCancel={handleDeleteClose}
        onConfirm={handleDelete}
        confirmationInProgress={deleteMutation.isLoading}
        open={deleteOpen}
        title="Delete Event"
        variant="error"
      />
    </>
  );
};

JobsMenu.propTypes = {
  job: PropTypes.any.isRequired,
};
